import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Icon } from '../Icon';
import ClickOutsideComponent from './clickOutside';

const IconWrapper = styled.div`
  display: flex;
  ${p =>
    p.border &&
    `border: 2px solid ${p.theme.colors.primary};
     border-radius: 100%;
     &:hover {
        box-shadow: 0 0 5px ${p.theme.colors.aux.darkGrey};
        border: 2px solid ${p.theme.colors.primary};
        border-color: ${p.theme.colors.aux.lightBlue};
      }`};
  padding: 5px;
  cursor: pointer;
  outline: none;
`;

const OptionsItem = styled.a`
  padding: 0.25em 0;
  display: flex;
  justify-content: flex-start;
  text-align: left;
  text-decoration: none;
  color: ${p =>
    p.darkmode ? p.theme.colors.aux.white : p.theme.colors.aux.darkGrey};
  font-family: inherit;
  &:hover,
  &:focus {
    outline: none;
    border-radius: 3px;
    text-decoration: underline;
    text-decoration-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const OptionsIcon = styled(Icon)`
  fill: ${p =>
    p.darkmode && !p.open
      ? p.theme.colors.aux.white
      : p.open
      ? p.theme.colors.primary
      : p.theme.colors.aux.charcoal};
`;

const OptionsDropdown = styled.div`
  display: ${p => (p.open ? 'flex' : 'none')};
  flex-direction: ${p => (p.expandable ? 'row' : 'column')};
  position: absolute;
  ${p => !p.expandable && 'padding: 1em'};
  border: 1px solid ${p => p.theme.colors.aux.lightGrey};
  border-top-width: 0;
  outline: none;
  font-weight: normal;
  box-shadow: 0 12px 12px ${p => p.theme.colors.aux.lightGrey};
  cursor: pointer;
  background: ${p =>
    p.darkmode ? p.theme.colors.aux.charcoal : p.theme.colors.aux.white};
  z-index: 999;
  font-size: 0.875rem;
  top: ${p => p.verticalOffset ?? '100%'};
  ${p => p.horizontalOffset && `right: ${p.horizontalOffset}`};
  width: ${p => p.width ?? 'auto'};
`;

const OptionsWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: ${p => (p.expandable ? 'flex-end' : 'center')};

  &:hover {
    div > ${OptionsIcon} {
      fill: ${p => p.theme.colors.primary};
    }

    > span {
      cursor: pointer;
      ${p => !p.open && `color: ${p.theme.colors.primary};`};
    }
  }
`;

const OptionsSectionTitle = styled.span`
  font-weight: bold;
  color: ${p =>
    p.darkmode ? p.theme.colors.aux.white : p.theme.colors.aux.darkGrey};
  border-bottom: 1px solid ${p => p.theme.colors.aux.lightGrey};
`;

const OptionsSectionWrapper = styled.div`
  padding: 1em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DropdownLabel = styled.span`
  color: ${p =>
    p.darkmode && !p.open
      ? p.theme.colors.aux.white
      : p.open
      ? p.theme.colors.primary
      : p.theme.colors.aux.charcoal};
  ${p => p.border && 'padding-right: .25em'};
`;

class Dropdown extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      open: false, // Ensure the dropdown is initially closed
    };
  }

  onClick = optionClick => e => {
    this.toggleClose();
    optionClick && optionClick(e);
  };
  toggleOpen = () => {
    this.setState(prevState => ({
      open: !prevState.open, // Toggle the dropdown state
    }));
  };
  toggleClose = () => {
    this.setState({
      open: false, // Close the dropdown
    });
  };

  renderOptionItems = (optionsArray, expandable, darkmode) => {
    return optionsArray?.map((o, i) => {
      return (
        <OptionsItem
          onClick={this.onClick(o.onClick)}
          onKeyPress={this.onClick(o.onClick)}
          tabIndex={0}
          key={`${o.title.toLowerCase().replace(/\s/g, '-')}-item`}
          href={o.link}
          target={o.target ?? '_blank'}
          data-test-id={`options-item-${i}`}
          expandable={expandable}
          darkmode={darkmode}
        >
          {o.title}
        </OptionsItem>
      );
    });
  };

  render() {
    const {
      options,
      iconSize,
      border,
      iconType,
      width,
      verticalOffset,
      horizontalOffset,
      label,
      darkmode,
    } = this.props;
    const { open } = this.state;
    const expandable = options.some(o => !!o.options?.length);

    return (
      <ClickOutsideComponent
        onClickOutside={this.toggleClose}
        open={open}
        label={label}
        darkmode={darkmode}
      >
        <OptionsWrapper darkmode={darkmode}>
          {label && (
            <DropdownLabel
              onClick={this.toggleOpen}
              onKeyPress={this.toggleOpen}
              open={open}
              border={border}
              darkmode={darkmode}
            >
              {label}
            </DropdownLabel>
          )}
          <IconWrapper
            tabIndex={0}
            border={border}
            onClick={this.toggleOpen}
            onKeyPress={this.toggleOpen}
            data-test-id="options-wrapper"
          >
            <OptionsIcon
              icon={iconType}
              height={iconSize}
              width={iconSize}
              darkmode={darkmode}
              open={open}
            />
          </IconWrapper>
          <OptionsDropdown
            width={width}
            open={open}
            expandable={expandable}
            verticalOffset={verticalOffset}
            horizontalOffset={horizontalOffset}
            darkmode={darkmode}
          >
            {expandable
              ? options.map(s => {
                  return (
                    <OptionsSectionWrapper
                      key={`${s.title
                        .toLowerCase()
                        .replace(/\s/g, '-')}-section`}
                    >
                      <OptionsSectionTitle darkmode={darkmode}>
                        {s.title}
                      </OptionsSectionTitle>
                      {this.renderOptionItems(s.options, expandable, darkmode)}
                    </OptionsSectionWrapper>
                  );
                })
              : this.renderOptionItems(options, expandable, darkmode)}
          </OptionsDropdown>
        </OptionsWrapper>
      </ClickOutsideComponent>
    );
  }
}

export const optionPropType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      link: PropTypes.string,
      target: PropTypes.string,
    })
  ),
  onClick: PropTypes.func,
  link: PropTypes.string,
  target: PropTypes.string,
});

Dropdown.defaultProps = {
  iconSize: '10',
  border: true,
  iconType: 'MoreVertical',
  options: [],
  width: '150px',
  verticalOffset: '100%',
  darkmode: false,
};

Dropdown.propTypes = {
  /**
   * List items have a Title and a Link or onClick function
   */
  options: PropTypes.arrayOf(optionPropType),
  /**
   * Size of the button in pixels - always a square
   */
  iconSize: PropTypes.string,
  /**
   * border or no border around the vertical dots
   */
  border: PropTypes.bool,
  /**
   * select the style of icon
   */
  iconType: PropTypes.string,
  /**
   * width of Options Dropdown
   */
  width: PropTypes.string,
  /**
   * vertical offset for the dropdown menu
   */
  verticalOffset: PropTypes.string,
  /**
   * horizontal offset for the dropdown menu
   */
  horizontalOffset: PropTypes.string,
  /**
   * label for the dropdown
   */
  label: PropTypes.string,
  /**
   * color theme for background and text
   */
  darkmode: PropTypes.bool,
};

export default Dropdown;
